import { startTransition, useLayoutEffect, useRef, useState } from "react";
import { classMerge } from "../utility/classMerge";
import { CELL_CLICKED, COL_ENTER_COMPLETE, COL_LEAVE_COMPLETE } from "./context/interface";
import { isCellHighlighted } from "./context/scaffoldSelection";
import {
  SCAFFOLD_ANIMATION_DURATION_MS,
  SCAFFOLD_ANIMATION_START_DELAY_MS,
  ScaffoldAnimationState,
} from "./interface";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";
import { ScaffoldZoneGrid } from "./ScaffoldZoneGrid";

export interface ScaffoldCellProps {
  /** Unique identifier for the row containing this cell. */
  rowId: string;
  /** Unique identifier for the column containing this cell. */
  colId: string;
  /** Optional temporary row height used for transient interactions such as drag resizing. */
  rowHeightOverride?: number;
  /** Optional style object to apply to the cell. */
  style?: React.CSSProperties;
}

/**
 * Cell component for the Scaffold, rendering a single cell based on the provided row and column identifiers.
 *
 * @param props - Component props
 * @param props.rowId - Unique identifier for the row
 * @param props.colId - Unique identifier for the column
 * @param props.rowHeightOverride - Optional temporary row height for interactions like drag resizing
 * @param props.style - Optional style object to apply to the cell
 * @returns A React element representing a scaffold cell with its row and column identifiers.
 */
export const ScaffoldCell = <T,>({ rowId, colId, rowHeightOverride, style }: ScaffoldCellProps) => {
  const scaffoldContext = useScaffold<T>();
  const isEnteringCol = scaffoldContext.state.enteringColIds.has(colId);
  const leavingCol = scaffoldContext.state.leavingCols.find((c) => c.id === colId);
  const col = scaffoldContext.settings.columns.find((c) => c.id === colId) ?? leavingCol;
  const row =
    scaffoldContext.settings.rows.find((r) => r.id === rowId) ??
    scaffoldContext.state.leavingRows.find((r) => r.id === rowId);

  /**
   * Determine initial state: entering columns start at "placed" (0px), leaving start at "open" (full).
   * All other columns start in the settled open state.
   *
   * @returns The initial animation state for the cell.
   */
  const getInitialState = (): ScaffoldAnimationState => {
    if (isEnteringCol) return "placed";
    if (leavingCol) return "open";
    return "open";
  };

  const [animationState, setAnimationState] = useState<ScaffoldAnimationState>(getInitialState());
  const timeoutsRef = useRef<number[]>([]);
  const wasLeavingColRef = useRef(Boolean(leavingCol));
  const hasNotifiedColLeaveCompleteRef = useRef<boolean>(false);
  const hasNotifiedColEnterCompleteRef = useRef<boolean>(false);

  /**
   * State machine for entering columns: placed (5ms delay) -> opening (animate for 200ms) -> open.
   * State machine for leaving columns: open -> closing (animate for 200ms) -> closed (unmount).
   */
  useLayoutEffect(() => {
    if (animationState !== "closing") {
      hasNotifiedColLeaveCompleteRef.current = false;
    }

    if (animationState !== "opening") {
      hasNotifiedColEnterCompleteRef.current = false;
    }

    if (animationState === "placed") {
      const id = window.setTimeout(() => {
        setAnimationState("opening");
      }, SCAFFOLD_ANIMATION_START_DELAY_MS);
      timeoutsRef.current.push(id);
    }
    if (animationState === "opening") {
      const id = window.setTimeout(() => {
        setAnimationState("open");
        /* istanbul ignore else */
        if (isEnteringCol && !hasNotifiedColEnterCompleteRef.current) {
          hasNotifiedColEnterCompleteRef.current = true;
          scaffoldContext.dispatch({ operation: COL_ENTER_COMPLETE, colId });
        }
      }, SCAFFOLD_ANIMATION_DURATION_MS);
      timeoutsRef.current.push(id);
    }
    if (animationState === "closing") {
      const id = window.setTimeout(() => {
        setAnimationState("closed");
        /* istanbul ignore else */
        if (leavingCol && !hasNotifiedColLeaveCompleteRef.current) {
          hasNotifiedColLeaveCompleteRef.current = true;
          scaffoldContext.dispatch({ operation: COL_LEAVE_COMPLETE, colId });
        }
      }, SCAFFOLD_ANIMATION_DURATION_MS);
      timeoutsRef.current.push(id);
    }

    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, [animationState, colId, isEnteringCol, leavingCol, scaffoldContext]);

  /**
   * When a column enters the leaving state, trigger the closing animation.
   */
  useLayoutEffect(() => {
    if (leavingCol && animationState === "open") {
      startTransition(() => setAnimationState("closing"));
    }
  }, [leavingCol, animationState]);

  /**
   * If a column stops being a leaving column before close completes,
   * reset so it can remain visible.
   */
  useLayoutEffect(() => {
    const wasLeavingCol = wasLeavingColRef.current;
    const isLeavingCol = Boolean(leavingCol);

    if (wasLeavingCol && !isLeavingCol) {
      startTransition(() => setAnimationState(isEnteringCol ? "placed" : "open"));
    }

    wasLeavingColRef.current = isLeavingCol;
  }, [isEnteringCol, leavingCol]);

  const colWidth = col?.width ?? 0;
  const rowHeight = rowHeightOverride ?? row?.height;
  const textAlignment = row?.textAlignment ?? col?.textAlignment ?? "default";

  const cellEditor = col?.cellComponent ?? scaffoldContext.settings.cellComponent;
  const cellValue =
    leavingCol || scaffoldContext.settings.cellData[rowId]?.[colId] === undefined
      ? cellEditor.blankT
      : scaffoldContext.settings.cellData[rowId]?.[colId];

  const CellEditor = cellEditor.Editor;

  /** Span information for this cell, if any span definitions are active. */
  const spanInfo = scaffoldContext.headerSpanMap?.[rowId]?.[colId];

  /**
   * Cells covered by a colSpan in the same row are removed from the DOM so the spanning
   * cell's width fills the correct space without duplication.
   */
  const isCoveredSameRow = spanInfo?.mode === "covered-same-row";

  /**
   * Cells covered by a rowSpan from a preceding row are rendered as invisible placeholders
   * to preserve flex column alignment in subsequent rows.
   */
  const isCoveredOtherRow = spanInfo?.mode === "covered-other-row";

  /** Effective width: use merged column width for spanning cells, otherwise normal column width. */
  const effectiveColWidth = spanInfo?.effectiveWidth ?? colWidth;

  /** Effective height: use merged row height for spanning cells, otherwise normal row height. */
  const effectiveRowHeight = spanInfo?.effectiveHeight ?? rowHeight;

  const displayWidth =
    animationState === "placed" || animationState === "closing" ? 0 : effectiveColWidth;

  const isSelected = isCellHighlighted(scaffoldContext.state.selection, rowId, colId);

  return animationState === "closed" || !col || isCoveredSameRow ? null : (
    <div
      className={classMerge([
        styles.cell,
        leavingCol ? styles.cellLeaving : undefined,
        isSelected ? styles.cellSelected : undefined,
        spanInfo?.hasRowSpan ? styles.cellWithRowSpan : undefined,
      ])}
      data-scaffold-col-id={colId}
      data-scaffold-row-id={rowId}
      style={{
        ...style,
        visibility: isCoveredOtherRow ? "hidden" : undefined,
        width: displayWidth,
        height: effectiveRowHeight,
        transition:
          animationState === "opening" || animationState === "closing"
            ? `width ${SCAFFOLD_ANIMATION_DURATION_MS}ms linear`
            : undefined,
      }}
      onClick={(event) => {
        if (!isCoveredOtherRow) {
          if (event.shiftKey) event.preventDefault();
          scaffoldContext.dispatch({
            operation: CELL_CLICKED,
            rowId,
            colId,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
          });
        }
      }}
    >
      <div className={styles.cellContent}>
        <CellEditor
          id={`${rowId}-${colId}`}
          colId={colId}
          editable={!scaffoldContext.settings.disabled}
          rowId={rowId}
          setValue={(newValue) => scaffoldContext.settings.onChangeCell?.(rowId, colId, newValue)}
          textAlignment={textAlignment}
          value={cellValue}
        />
      </div>
      <ScaffoldZoneGrid
        rowId={rowId}
        colId={colId}
      />
    </div>
  );
};

ScaffoldCell.displayName = "ScaffoldCell";
